// Cockpit Démo — Professionnel (Aqua Piscines / Jean-Marc Fontaine)
// Page de démonstration pré-remplie pour montrer la valeur du cockpit pro aux visiteurs non connectés
// Tutoiement systématique — Français uniquement

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Briefcase, Users, FileText, TrendingUp, Bell, Settings, Plus, ChevronRight, MapPin, Phone, Mail, Calendar, Euro, Clock, CheckCircle, AlertCircle, Bot, Zap, MessageSquare, PieChart } from 'lucide-react';

// Mock data Aqua Piscines
const AQUA_DATA = {
  company: {
    name: 'Aqua Piscines',
    owner: 'Jean-Marc Fontaine',
    logo: '🏊',
    email: 'contact@aqua-piscines.fr',
    phone: '04 72 XX XX XX',
    address: '45 Avenue des Piscines, 69100 Villeurbanne',
    siret: '123 456 789 00012',
    memberSince: 'Janvier 2024',
  },
  stats: {
    leadsEnCours: 12,
    devisEnvoyes: 8,
    chantiersActifs: 4,
    caThisMois: 47500,
  },
  pipeline: [
    { stage: 'Nouveaux leads', count: 5, color: '#3B82F6', value: 75000 },
    { stage: 'Qualification', count: 3, color: '#8B5CF6', value: 45000 },
    { stage: 'Visite technique', count: 2, color: '#F59E0B', value: 32000 },
    { stage: 'Devis envoyé', count: 4, color: '#E8650A', value: 58000 },
    { stage: 'Négociation', count: 2, color: '#10B981', value: 28000 },
  ],
  leads: [
    { 
      id: 1, 
      name: 'Famille Dubois', 
      project: 'Piscine 8x4 + terrasse',
      budget: '45 000 €',
      stage: 'Visite technique',
      stageColor: '#F59E0B',
      location: 'Lyon 6ème',
      date: 'RDV: 25 Mars 14h',
      hot: true,
    },
    { 
      id: 2, 
      name: 'M. Bernard',
      project: 'Rénovation liner',
      budget: '8 500 €',
      stage: 'Devis envoyé',
      stageColor: '#E8650A',
      location: 'Caluire',
      date: 'Relance: 22 Mars',
      hot: false,
    },
    { 
      id: 3, 
      name: 'Mme Petit',
      project: 'Piscine enterrée 10x5',
      budget: '65 000 €',
      stage: 'Qualification',
      stageColor: '#8B5CF6',
      location: 'Écully',
      date: 'Appel: Aujourd\'hui',
      hot: true,
    },
  ],
  quotes: [
    { id: 1, client: 'Famille Laurent', project: 'Piscine 9x4.5', amount: 52000, status: 'en_attente', sentDate: '18 Mars 2026' },
    { id: 2, client: 'M. Moreau', project: 'Spa + local technique', amount: 18500, status: 'accepté', sentDate: '12 Mars 2026' },
    { id: 3, client: 'Copro Les Tilleuls', project: 'Rénovation collective', amount: 35000, status: 'en_attente', sentDate: '15 Mars 2026' },
  ],
  chantiers: [
    { id: 1, client: 'M. & Mme Roux', project: 'Piscine béton 8x4', progress: 75, startDate: '4 Mars 2026', endDate: '10 Avril 2026', nextStep: 'Pose margelles' },
    { id: 2, client: 'Hôtel Bellecour', project: 'Rénovation spa', progress: 40, startDate: '15 Mars 2026', endDate: '5 Avril 2026', nextStep: 'Installation pompes' },
  ],
  aiAgents: [
    { id: 1, name: 'Agent Qualification', status: 'actif', leads: 23, icon: '🤖', description: 'Qualifie automatiquement les leads entrants' },
    { id: 2, name: 'Agent Relance', status: 'actif', leads: 8, icon: '📞', description: 'Relance les devis non signés après 7 jours' },
    { id: 3, name: 'Agent Avis', status: 'pause', leads: 0, icon: '⭐', description: 'Demande des avis après chaque chantier' },
  ],
  notifications: [
    { id: 1, type: 'lead', message: 'Nouveau lead: Piscine 8x4 à Lyon 3ème', time: 'Il y a 30 min', unread: true },
    { id: 2, type: 'devis', message: 'Devis accepté: M. Moreau (18 500€)', time: 'Il y a 2h', unread: true },
    { id: 3, type: 'rdv', message: 'RDV technique demain 10h — Mme Garnier', time: 'Il y a 5h', unread: false },
  ],
};

export default function DemoProCockpitPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');

  const getStatusColor = (status) => {
    switch (status) {
      case 'en_attente': return { bg: '#FEF3C7', text: '#92400E', label: 'En attente' };
      case 'accepté': return { bg: '#DCFCE7', text: '#166534', label: 'Accepté' };
      case 'refusé': return { bg: '#FEE2E2', text: '#991B1B', label: 'Refusé' };
      default: return { bg: '#F3F4F6', text: '#374151', label: status };
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: PieChart },
    { id: 'pipeline', label: 'Pipeline', icon: TrendingUp },
    { id: 'quotes', label: 'Devis', icon: FileText },
    { id: 'ai', label: 'Agents IA', icon: Bot },
  ];

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#F8FAFC',
      paddingBottom: '100px',
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1E3A5F 0%, #2D4A6F 100%)',
        padding: '16px 20px',
        paddingTop: 'max(16px, env(safe-area-inset-top))',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: 'rgba(255,255,255,0.15)',
              border: 'none',
              borderRadius: '10px',
              padding: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            data-testid="back-button"
          >
            <ArrowLeft size={20} color="#fff" />
          </button>
          <div style={{ flex: 1 }}>
            <div style={{
              display: 'inline-block',
              background: 'rgba(255,255,255,0.15)',
              borderRadius: '20px',
              padding: '4px 12px',
              fontSize: '11px',
              fontWeight: '600',
              color: '#fff',
              marginBottom: '4px',
            }}>
              MODE DÉMO
            </div>
            <h1 style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: '20px',
              fontWeight: '700',
              color: '#fff',
              margin: 0,
            }}>Cockpit Pro</h1>
          </div>
          <div style={{ position: 'relative' }}>
            <Bell size={22} color="#fff" style={{ opacity: 0.9 }} />
            <span style={{
              position: 'absolute',
              top: -4,
              right: -4,
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              background: '#EF4444',
              fontSize: '10px',
              fontWeight: '700',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>2</span>
          </div>
        </div>

        {/* Company Card */}
        <div style={{
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '16px',
          padding: '16px',
          backdropFilter: 'blur(10px)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '50px',
              height: '50px',
              borderRadius: '12px',
              background: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
            }}>{AQUA_DATA.company.logo}</div>
            <div style={{ flex: 1 }}>
              <div style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: '16px',
                fontWeight: '700',
                color: '#fff',
              }}>{AQUA_DATA.company.name}</div>
              <div style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '12px',
                color: 'rgba(255,255,255,0.8)',
              }}>{AQUA_DATA.company.owner}</div>
            </div>
            <div style={{
              background: '#10B981',
              borderRadius: '8px',
              padding: '6px 10px',
            }}>
              <div style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '10px',
                color: 'rgba(255,255,255,0.8)',
              }}>Ce mois</div>
              <div style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: '14px',
                fontWeight: '700',
                color: '#fff',
              }}>{formatCurrency(AQUA_DATA.stats.caThisMois)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '8px',
        padding: '16px',
        marginTop: '-8px',
      }}>
        {[
          { value: AQUA_DATA.stats.leadsEnCours, label: 'Leads', color: '#3B82F6' },
          { value: AQUA_DATA.stats.devisEnvoyes, label: 'Devis', color: '#E8650A' },
          { value: AQUA_DATA.stats.chantiersActifs, label: 'Chantiers', color: '#10B981' },
          { value: '238K€', label: 'Pipeline', color: '#8B5CF6', isText: true },
        ].map((stat, idx) => (
          <div key={idx} style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '12px 8px',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}>
            <div style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: stat.isText ? '14px' : '20px',
              fontWeight: '700',
              color: stat.color,
            }}>{stat.value}</div>
            <div style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '10px',
              color: '#6B7280',
            }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        padding: '0 16px',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 14px',
                borderRadius: '10px',
                border: 'none',
                background: isActive ? '#1E3A5F' : '#fff',
                color: isActive ? '#fff' : '#6B7280',
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '13px',
                fontWeight: isActive ? '600' : '500',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                boxShadow: isActive ? '0 2px 8px rgba(30, 58, 95, 0.3)' : '0 1px 3px rgba(0,0,0,0.08)',
              }}
              data-testid={`tab-${tab.id}`}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div style={{ padding: '16px' }}>
        {activeTab === 'dashboard' && (
          <>
            {/* Notifications */}
            {AQUA_DATA.notifications.some(n => n.unread) && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1F2937',
                  marginBottom: '12px',
                }}>Notifications</h3>
                {AQUA_DATA.notifications.filter(n => n.unread).map((notif) => (
                  <div key={notif.id} style={{
                    background: '#fff',
                    borderRadius: '12px',
                    padding: '14px',
                    marginBottom: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    borderLeft: '3px solid #1E3A5F',
                  }}>
                    <div style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '13px',
                      color: '#1F2937',
                      marginBottom: '4px',
                    }}>{notif.message}</div>
                    <div style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '11px',
                      color: '#9CA3AF',
                    }}>{notif.time}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Pipeline mini */}
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: '14px',
                fontWeight: '600',
                color: '#1F2937',
                marginBottom: '12px',
              }}>Pipeline commercial</h3>
              <div style={{
                background: '#fff',
                borderRadius: '16px',
                padding: '16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              }}>
                {AQUA_DATA.pipeline.map((stage, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: idx < AQUA_DATA.pipeline.length - 1 ? '12px' : 0,
                  }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: stage.color,
                    }} />
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: '12px',
                        color: '#6B7280',
                      }}>{stage.stage}</div>
                    </div>
                    <div style={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#1F2937',
                    }}>{stage.count}</div>
                    <div style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '12px',
                      color: stage.color,
                      fontWeight: '600',
                    }}>{formatCurrency(stage.value)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Leads chauds */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1F2937',
                }}>Leads prioritaires 🔥</h3>
                <button style={{
                  background: 'none',
                  border: 'none',
                  color: '#1E3A5F',
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}>Voir tout →</button>
              </div>
              {AQUA_DATA.leads.filter(l => l.hot).map((lead) => (
                <div key={lead.id} style={{
                  background: '#fff',
                  borderRadius: '16px',
                  padding: '16px',
                  marginBottom: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  borderLeft: '3px solid #EF4444',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div>
                      <h4 style={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1F2937',
                        margin: 0,
                      }}>{lead.name}</h4>
                      <div style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: '12px',
                        color: '#6B7280',
                        marginTop: '2px',
                      }}>{lead.project}</div>
                    </div>
                    <span style={{
                      background: lead.stageColor + '20',
                      color: lead.stageColor,
                      padding: '4px 10px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                    }}>{lead.stage}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '12px',
                      color: '#6B7280',
                    }}>
                      <MapPin size={14} /> {lead.location}
                    </div>
                    <div style={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: '15px',
                      fontWeight: '700',
                      color: '#1E3A5F',
                    }}>{lead.budget}</div>
                  </div>
                  <div style={{
                    marginTop: '10px',
                    padding: '8px 12px',
                    background: '#F8FAFC',
                    borderRadius: '8px',
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: '12px',
                    color: '#E8650A',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}>
                    <Calendar size={14} /> {lead.date}
                  </div>
                </div>
              ))}
            </div>

            {/* Chantiers actifs */}
            <div>
              <h3 style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: '14px',
                fontWeight: '600',
                color: '#1F2937',
                marginBottom: '12px',
              }}>Chantiers en cours</h3>
              {AQUA_DATA.chantiers.map((chantier) => (
                <div key={chantier.id} style={{
                  background: '#fff',
                  borderRadius: '16px',
                  padding: '16px',
                  marginBottom: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                      <h4 style={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1F2937',
                        margin: '0 0 4px 0',
                      }}>{chantier.client}</h4>
                      <div style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: '12px',
                        color: '#6B7280',
                      }}>{chantier.project}</div>
                    </div>
                    <span style={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: '14px',
                      fontWeight: '700',
                      color: '#10B981',
                    }}>{chantier.progress}%</span>
                  </div>
                  <div style={{ height: '6px', background: '#E5E7EB', borderRadius: '3px', overflow: 'hidden', marginBottom: '10px' }}>
                    <div style={{ width: `${chantier.progress}%`, height: '100%', background: '#10B981', borderRadius: '3px' }} />
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: '11px',
                    color: '#6B7280',
                  }}>
                    <span>Prochaine étape: {chantier.nextStep}</span>
                    <span>Fin prévue: {chantier.endDate}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {activeTab === 'pipeline' && (
          <div>
            <h3 style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: '16px',
              fontWeight: '600',
              color: '#1F2937',
              marginBottom: '16px',
            }}>Ton pipeline commercial</h3>
            
            {/* Pipeline Kanban simplifié */}
            <div style={{ overflowX: 'auto', paddingBottom: '16px' }}>
              <div style={{ display: 'flex', gap: '12px', minWidth: '600px' }}>
                {AQUA_DATA.pipeline.map((stage, idx) => (
                  <div key={idx} style={{
                    flex: 1,
                    minWidth: '140px',
                    background: '#fff',
                    borderRadius: '16px',
                    padding: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '12px',
                    }}>
                      <div style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        background: stage.color,
                      }} />
                      <div style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: '11px',
                        fontWeight: '600',
                        color: '#6B7280',
                      }}>{stage.stage}</div>
                    </div>
                    <div style={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: '24px',
                      fontWeight: '700',
                      color: '#1F2937',
                      marginBottom: '4px',
                    }}>{stage.count}</div>
                    <div style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '13px',
                      fontWeight: '600',
                      color: stage.color,
                    }}>{formatCurrency(stage.value)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Leads liste */}
            <h4 style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: '14px',
              fontWeight: '600',
              color: '#1F2937',
              marginTop: '20px',
              marginBottom: '12px',
            }}>Tous tes leads</h4>
            {AQUA_DATA.leads.map((lead) => (
              <div key={lead.id} style={{
                background: '#fff',
                borderRadius: '16px',
                padding: '16px',
                marginBottom: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h4 style={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#1F2937',
                      margin: '0 0 4px 0',
                    }}>{lead.name} {lead.hot && '🔥'}</h4>
                    <div style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '12px',
                      color: '#6B7280',
                    }}>{lead.project}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{
                      display: 'inline-block',
                      background: lead.stageColor + '20',
                      color: lead.stageColor,
                      padding: '4px 10px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                      marginBottom: '4px',
                    }}>{lead.stage}</span>
                    <div style={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: '14px',
                      fontWeight: '700',
                      color: '#1E3A5F',
                    }}>{lead.budget}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'quotes' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: '16px',
                fontWeight: '600',
                color: '#1F2937',
              }}>Tes devis</h3>
              <button style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: '#1E3A5F',
                color: '#fff',
                border: 'none',
                borderRadius: '10px',
                padding: '10px 16px',
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
              }}>
                <Plus size={16} /> Nouveau devis
              </button>
            </div>
            {AQUA_DATA.quotes.map((quote) => {
              const status = getStatusColor(quote.status);
              return (
                <div key={quote.id} style={{
                  background: '#fff',
                  borderRadius: '16px',
                  padding: '16px',
                  marginBottom: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                    <div>
                      <h4 style={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1F2937',
                        margin: '0 0 4px 0',
                      }}>{quote.client}</h4>
                      <div style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: '12px',
                        color: '#6B7280',
                      }}>{quote.project}</div>
                    </div>
                    <span style={{
                      background: status.bg,
                      color: status.text,
                      padding: '4px 10px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                    }}>{status.label}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: '18px',
                      fontWeight: '700',
                      color: '#1F2937',
                    }}>{formatCurrency(quote.amount)}</div>
                    <div style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '11px',
                      color: '#9CA3AF',
                    }}>Envoyé le {quote.sentDate}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'ai' && (
          <div>
            <h3 style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: '16px',
              fontWeight: '600',
              color: '#1F2937',
              marginBottom: '8px',
            }}>Tes agents IA</h3>
            <p style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '13px',
              color: '#6B7280',
              marginBottom: '20px',
            }}>Automatise ton business pendant que tu travailles sur le terrain</p>
            
            {AQUA_DATA.aiAgents.map((agent) => (
              <div key={agent.id} style={{
                background: '#fff',
                borderRadius: '16px',
                padding: '16px',
                marginBottom: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                border: agent.status === 'actif' ? '1px solid #10B98133' : '1px solid #E5E7EB',
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: agent.status === 'actif' ? '#DCFCE7' : '#F3F4F6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                  }}>{agent.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <h4 style={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1F2937',
                        margin: 0,
                      }}>{agent.name}</h4>
                      <span style={{
                        background: agent.status === 'actif' ? '#DCFCE7' : '#F3F4F6',
                        color: agent.status === 'actif' ? '#166534' : '#6B7280',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '600',
                      }}>{agent.status === 'actif' ? '● Actif' : '○ Pause'}</span>
                    </div>
                    <p style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '12px',
                      color: '#6B7280',
                      margin: '0 0 8px 0',
                    }}>{agent.description}</p>
                    {agent.status === 'actif' && agent.leads > 0 && (
                      <div style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: '12px',
                        color: '#10B981',
                        fontWeight: '600',
                      }}>
                        <Zap size={12} style={{ display: 'inline', marginRight: '4px' }} />
                        {agent.leads} leads traités ce mois
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {/* CTA Agent */}
            <div style={{
              background: 'linear-gradient(135deg, #1E3A5F 0%, #2D4A6F 100%)',
              borderRadius: '16px',
              padding: '20px',
              marginTop: '20px',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: '28px', marginBottom: '12px' }}>🤖</div>
              <h4 style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: '15px',
                fontWeight: '700',
                color: '#fff',
                margin: '0 0 8px 0',
              }}>Crée ton propre agent IA</h4>
              <p style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '12px',
                color: 'rgba(255,255,255,0.8)',
                margin: '0 0 16px 0',
              }}>Automatise n'importe quelle tâche répétitive</p>
              <button style={{
                background: '#fff',
                color: '#1E3A5F',
                border: 'none',
                borderRadius: '10px',
                padding: '12px 24px',
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '13px',
                fontWeight: '700',
                cursor: 'pointer',
              }}>Découvrir →</button>
            </div>
          </div>
        )}
      </div>

      {/* CTA Bottom */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: '#fff',
        borderTop: '1px solid #E5E7EB',
        padding: '12px 20px',
        paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
        zIndex: 100,
      }}>
        <a
          href="https://calendly.com/romain-trallero/rdv-romain-30min?month=2026-03"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'block',
            width: '100%',
            background: '#22c55e',
            color: '#fff',
            border: 'none',
            borderRadius: '14px',
            padding: '14px',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: '15px',
            fontWeight: '700',
            cursor: 'pointer',
            textAlign: 'center',
            textDecoration: 'none',
          }}
          data-testid="cta-calendly-pro"
        >
          💼 Réserve ton créneau avec Romain
          <span style={{
            display: 'block',
            fontSize: '11px',
            color: 'rgba(255,255,255,0.8)',
            fontWeight: '400',
            marginTop: '2px',
          }}>Paramétrage clé en main en 72h</span>
        </a>
      </div>
    </div>
  );
}
